import React, { createContext, useCallback, useMemo } from 'react';
import type { Bundle, I18nContextValue, Locale } from './types';

export interface I18nProviderProps {
  /** Active locale translations; keys are "namespace.key" */
  bundle: Bundle;
  /** Active locale code */
  locale: Locale;
  /** Fallback locale code (for display only in phase 1) */
  fallback: Locale;
  /** Translations for the fallback locale */
  fallbackBundle?: Bundle;
  /** Called when a child requests a locale change. Host app swaps the bundle prop. */
  onLocaleChange?: (next: Locale) => void;
  children: React.ReactNode;
}

/**
 * React context for the i18n system. Exported for testing.
 * Use `useTranslation()` rather than consuming this context directly.
 */
export const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * Provider that makes translation bundles available to the component tree.
 * The provider does NOT own bundle loading — the host app swaps the `bundle`
 * prop on the next render after `onLocaleChange` fires.
 */
export function I18nProvider({
  bundle,
  locale,
  fallbackBundle,
  onLocaleChange,
  children,
}: I18nProviderProps): React.JSX.Element {
  const setLocale = useCallback(
    (next: Locale) => {
      onLocaleChange?.(next);
    },
    [onLocaleChange],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ bundle, locale, fallbackBundle, setLocale }),
    [bundle, locale, fallbackBundle, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
